import { useEffect, useMemo, useState } from 'react';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import type { ServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';

const provenanceFhirService = new FhirService('Provenance');
const documentReferenceFhirService = new FhirService('DocumentReference');
const communicationFhirService = new FhirService('Communication');
const questionnaireResponseFhirService = new FhirService(
  'QuestionnaireResponse',
);

type ResourceRef = { resourceType: string; id: string };

export type ExtractedResourceSource = {
  document: fhir.DocumentReference | null;
  communication: fhir.Communication | null;
  questionnaireResponse: fhir.QuestionnaireResponse | null;
};

export type ResolvedExtractedResourceSource = ExtractedResourceSource & {
  sourceReference: string | null;
};

type ResolveDeps = {
  auth: ServiceAuth | undefined;
  accountId: string | undefined;
  projectId: string | undefined;
};

const itemKey = (item: ResourceRef) => `${item.resourceType}/${item.id}`;
const referenceKey = (projectId: string, item: ResourceRef) =>
  `${projectId}::${itemKey(item)}`;
const documentKey = (accountId: string, reference: string) =>
  `${accountId}::${reference}`;

/**
 * Module-level caches so a resource's source document survives the review modal
 * being closed and reopened, and so the same provenance/document is never
 * fetched twice across the needs-review list, the modal source pane, and the
 * top-of-list preloader. Provenance and source documents are effectively
 * immutable for an extracted resource, so caching them indefinitely is safe.
 */
const referenceCache = new Map<string, string | null>();
const referenceInflight = new Map<string, Promise<string | null>>();
const documentCache = new Map<string, ExtractedResourceSource>();
const documentInflight = new Map<string, Promise<ExtractedResourceSource>>();

const getSourceReference = (
  provenance: fhir.Provenance | null,
): string | null =>
  provenance?.entity?.find(entity => entity.role === 'source')?.whatReference
    ?.reference ?? null;

const parseReference = (reference: string | null): ResourceRef | null => {
  const match = reference?.match(/^([A-Za-z]+)\/(.+)$/);
  return match ? { resourceType: match[1], id: match[2] } : null;
};

const depsReady = (
  deps: ResolveDeps,
): deps is { auth: ServiceAuth; accountId: string; projectId: string } =>
  !!deps.auth && !!deps.accountId && !!deps.projectId;

/**
 * Resolve (and cache) just the source reference for an extracted resource from
 * its Provenance. Used to group resources that share a source document without
 * fetching the document itself.
 */
export const resolveExtractedResourceSourceReference = (
  deps: ResolveDeps,
  item: ResourceRef,
): Promise<string | null> => {
  if (!depsReady(deps)) {
    return Promise.resolve(null);
  }

  const { auth, accountId, projectId } = deps;
  const key = referenceKey(projectId, item);
  if (referenceCache.has(key)) {
    return Promise.resolve(referenceCache.get(key) ?? null);
  }
  const existing = referenceInflight.get(key);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<string | null> => {
    try {
      const response = await provenanceFhirService.search(auth)(
        accountId,
        projectId,
        { target: itemKey(item), _count: 1 },
      );
      const provenance = response?.data?.entry?.[0]?.resource ?? null;
      const reference = getSourceReference(provenance);
      referenceCache.set(key, reference);
      return reference;
    } catch {
      // Don't cache failures so a later attempt can retry.
      return null;
    } finally {
      referenceInflight.delete(key);
    }
  })();

  referenceInflight.set(key, promise);
  return promise;
};

const resolveSourceDocument = (
  deps: ResolveDeps,
  reference: string,
): Promise<ExtractedResourceSource> => {
  if (!depsReady(deps)) {
    return Promise.resolve({
      document: null,
      communication: null,
      questionnaireResponse: null,
    });
  }

  const { auth, accountId } = deps;
  const key = documentKey(accountId, reference);
  const cached = documentCache.get(key);
  if (cached) {
    return Promise.resolve(cached);
  }
  const existing = documentInflight.get(key);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<ExtractedResourceSource> => {
    const parsed = parseReference(reference);
    try {
      if (parsed?.resourceType === 'DocumentReference') {
        const response = await documentReferenceFhirService.getById(auth)(
          accountId,
          parsed.id,
        );
        const resolved = {
          document: response?.data ?? null,
          communication: null,
          questionnaireResponse: null,
        };
        documentCache.set(key, resolved);
        return resolved;
      }
      if (parsed?.resourceType === 'Communication') {
        const response = await communicationFhirService.getById(auth)(
          accountId,
          parsed.id,
        );
        const resolved = {
          document: null,
          communication: response?.data ?? null,
          questionnaireResponse: null,
        };
        documentCache.set(key, resolved);
        return resolved;
      }
      if (parsed?.resourceType === 'QuestionnaireResponse') {
        const response = await questionnaireResponseFhirService.getById(auth)(
          accountId,
          parsed.id,
        );
        const resolved = {
          document: null,
          communication: null,
          questionnaireResponse: response?.data ?? null,
        };
        documentCache.set(key, resolved);
        return resolved;
      }
      return {
        document: null,
        communication: null,
        questionnaireResponse: null,
      };
    } catch {
      return {
        document: null,
        communication: null,
        questionnaireResponse: null,
      };
    } finally {
      documentInflight.delete(key);
    }
  })();

  documentInflight.set(key, promise);
  return promise;
};

/**
 * Resolve (and cache) the full source for an extracted resource: its
 * provenance source reference plus the source DocumentReference/Communication.
 */
export const resolveExtractedResourceSource = async (
  deps: ResolveDeps,
  item: ResourceRef,
): Promise<ResolvedExtractedResourceSource> => {
  const sourceReference = await resolveExtractedResourceSourceReference(
    deps,
    item,
  );
  if (!sourceReference) {
    return {
      sourceReference: null,
      document: null,
      communication: null,
      questionnaireResponse: null,
    };
  }
  const { document, communication, questionnaireResponse } =
    await resolveSourceDocument(deps, sourceReference);
  return { sourceReference, document, communication, questionnaireResponse };
};

export const getCachedSourceReference = (
  projectId: string | undefined,
  item: ResourceRef,
): string | null | undefined =>
  projectId ? referenceCache.get(referenceKey(projectId, item)) : undefined;

const peekResolvedSource = (
  deps: ResolveDeps,
  item: ResourceRef | null,
): ResolvedExtractedResourceSource | undefined => {
  if (!item || !deps.projectId) {
    return undefined;
  }
  const refKey = referenceKey(deps.projectId, item);
  if (!referenceCache.has(refKey)) {
    return undefined;
  }
  const sourceReference = referenceCache.get(refKey) ?? null;
  if (!sourceReference) {
    return {
      sourceReference: null,
      document: null,
      communication: null,
      questionnaireResponse: null,
    };
  }
  if (!deps.accountId) {
    return undefined;
  }
  const document = documentCache.get(
    documentKey(deps.accountId, sourceReference),
  );
  if (!document) {
    return undefined;
  }
  return { sourceReference, ...document };
};

/**
 * Resolve the source document/communication for a single extracted resource,
 * reading from (and populating) the shared cache. While resolution is in
 * flight `isLoading` is true so the consumer can show a loading indicator
 * rather than an "unknown source" placeholder. Cached sources resolve
 * synchronously, so reopening the review modal is instant.
 */
export const useExtractedResourceSource = (
  item: ResourceRef | null,
): {
  source: ExtractedResourceSource | null;
  sourceReference: string | null;
  isLoading: boolean;
} => {
  const auth = useServiceAuth();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const deps = useMemo<ResolveDeps>(
    () => ({ auth, accountId, projectId }),
    [auth, accountId, projectId],
  );
  const enabled = !!item && depsReady(deps);

  const cached = peekResolvedSource(deps, item);
  const [resolved, setResolved] =
    useState<ResolvedExtractedResourceSource | null>(cached ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled && !cached);

  const key = item && projectId ? referenceKey(projectId, item) : null;
  useEffect(() => {
    if (!item || !depsReady(deps)) {
      setResolved(null);
      setIsLoading(false);
      return;
    }

    const cachedNow = peekResolvedSource(deps, item);
    if (cachedNow) {
      setResolved(cachedNow);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    void resolveExtractedResourceSource(deps, item).then(next => {
      if (!cancelled) {
        setResolved(next);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // `deps` is memoized on the same primitives as `key`; `item` identity is
    // captured by `key` (resourceType/id/project).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return {
    source: resolved
      ? {
          document: resolved.document,
          communication: resolved.communication,
          questionnaireResponse: resolved.questionnaireResponse,
        }
      : null,
    sourceReference: resolved?.sourceReference ?? null,
    isLoading,
  };
};

/**
 * Warm the source-document cache for the given resources (e.g. the top of the
 * needs-review list) so opening the review modal is instant.
 */
export const useExtractedResourceSourcePreload = (
  items: ResourceRef[],
): void => {
  const auth = useServiceAuth();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const itemsKey = items.map(itemKey).join(',');

  useEffect(() => {
    const deps: ResolveDeps = { auth, accountId, projectId };
    if (!depsReady(deps)) {
      return;
    }
    items.forEach(item => {
      void resolveExtractedResourceSource(deps, item);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, auth, accountId, projectId]);
};

export const __resetExtractedResourceSourceCacheForTests = (): void => {
  referenceCache.clear();
  referenceInflight.clear();
  documentCache.clear();
  documentInflight.clear();
};
